import AbstractComponent from './AbstractComponent';

/**
 * Represents a single slide.
 */
type Slide = {
  /**
   * The HTML element of the slide.
   */
  $slide: HTMLElement,

  /**
   * The index of the slide.
   */
  index: number,
};

/**
 * Manages the trackers component.
 */
export default class Trackers extends AbstractComponent {
  /**
   * The spacing between the slide and the edge of the screen on mobile, in
   * pixels.
   */
  public static readonly SPACING_BETWEEN_SCREEN_MOBILE = 24;

  /**
   * The spacing between the slides, in pixels.
   */
  public static readonly SPACING_BETWEEN_SLIDES = 16;

  /**
   * The CSS value of the `transition` property that should be applied when
   * moving the slider.
   */
  public static readonly TRANSITION = 'left .15s ease-in-out';

  /**
   * The HTML element of the outer wrapper of the slider.
   */
  private $sliderWrapper: HTMLElement;

  /**
   * The HTML element of the slider.
   */
  private $slider: HTMLElement;

  /**
   * The currently shown slide.
   */
  private currentSlide: Slide | undefined = undefined;

  /**
   * The available slides.
   */
  private slides: Slide[] = [];

  /**
   * The position of the slider that was achieved using touch, in pixels.
   */
  private touchPosition?: number;

  /**
   * The starting X-coordinate of the touch action.
   */
  private touchStartX?: number;

  /**
   * The timestamp at which the touch action was started.
   */
  private touchStartedAt?: number;

  /**
   * Flag to indicate if the user is currently swiping.
   */
  private isSwiping: boolean = false;

  /**
   * Flag to indicate if the touch action should be canceled.
   */
  private isTouchCanceled: boolean = false;

  /**
   * The offset from the sides of the screen, in pixels.
   */
  private sliderOffset: number;

  /**
   * The width of a single slide, in pixels.
   */
  private slideWidth: number;

  /**
   * Initializes the testimonials component.
   */
  public initialize(): void {
    this.$sliderWrapper = document.querySelector('.trackers__outer-wrapper');
    this.$slider = document.querySelector('.trackers__inner-wrapper');

    // Process the slides.
    this.$slider.querySelectorAll('.trackers__tracker').forEach(($slide, index) => {
      const slide: Slide = {
        $slide: $slide as HTMLElement,
        index,
      };

      ($slide as HTMLElement).addEventListener('click', (event) => {
        this.onSlideClick(event);
      });

      $slide.setAttribute('data-slide', `${index}`);

      this.slides.push(slide);
    });

    // Add the necessary event listeners to the slider wrapper.
    this.$sliderWrapper.addEventListener('transitionend', () => this.onSliderTransitionEnd());
    this.$sliderWrapper.addEventListener('pointercancel', () => this.onPointerCancel());
    this.$sliderWrapper.addEventListener('touchstart', (event) => this.onTouchStart(event));
    this.$sliderWrapper.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
    this.$sliderWrapper.addEventListener('touchend', (event) => this.onTouchEnd(event));

    // Move the slider to the first slide.
    this.calculateSlideWidth();
    this.moveToSlide(0);
  }

  /**
   * Calculates the slide width.
   */
  private calculateSlideWidth(): void {
    const { width } = this.slides[0].$slide.getBoundingClientRect();

    this.sliderOffset = (window.innerWidth - width) / 2;
    this.slideWidth = width + Trackers.SPACING_BETWEEN_SLIDES;
  }

  /**
   * Moves the slider to a specific slide.
   *
   * There are three possible options to pass into this method:
   *  - 'next':     Moves to the next slide. If the slider is already at the end
   *                of the available slides, it will wrap over to the beginning.
   *  - 'previous': Moves to the previous slide. If the slider is already at the
   *                start of the available slides, it will wrap over to the end.
   *  - a number:   Moves to the slide with that index.
   *
   * @param slide The slide to switch to
   */
  private moveToSlide(slide: 'next' | 'previous' | number): void {
    // Remove the active state from the current slide.
    if (this.currentSlide) {
      this.currentSlide.$slide.classList.remove('is-active');
    }

    // Determine the next slide to go to.
    switch (slide) {
    case 'next':
      if (this.currentSlide.index >= this.slides.length - 1) {
        this.currentSlide = this.slides[0];
      } else {
        this.currentSlide = this.slides[this.currentSlide.index + 1];
      }
      break;

    case 'previous':
      if (this.currentSlide.index === 0) {
        this.currentSlide = this.slides[this.slides.length - 1];
      } else {
        this.currentSlide = this.slides[this.currentSlide.index - 1];
      }
      break;

    default:
      this.currentSlide = this.slides[slide];
      break;
    }

    // Switch to the new slide.
    this.currentSlide.$slide.classList.add('is-active');

    this.moveSlider();
  }

  /**
   * Calculates the position for the slider, and moves it to that position.
   *
   * @param instant Whether or not the slider must be moved instantaneously
   */
  private moveSlider(instant: boolean = false): void {
    if (!instant) {
      this.$slider.style.transition = Trackers.TRANSITION;
    }

    // Update the slider's styling.
    this.$slider.style.left = `${-(this.slideWidth * this.currentSlide.index) + this.sliderOffset}px`;
  }

  /**
   * Called when the window is resized.
   */
  public onWindowResize(): void {
    this.calculateSlideWidth();
    this.moveSlider(true);
  }

  /**
   * Called when the user clicks on a slide.
   *
   * @param event The event arguments
   */
  private onSlideClick(event: MouseEvent): void {
    event.preventDefault();

    // The user must not be able to click on a slide to move to it on mobile.
    if (window.innerWidth < AbstractComponent.BREAKPOINT_MD) {
      return;
    }

    // Find the root element of the slide, that has the `data-slide` attribute.
    let $slide = event.target as HTMLElement;

    while (!$slide.classList.contains('trackers__tracker')) {
      $slide = $slide.parentElement;
    }

    // Move the slider to the slide that the user clicked on.
    this.moveToSlide(parseInt($slide.getAttribute('data-slide')));
  }

  /**
   * Called when the transition finishes on the slider element.
   *
   * Removes the `transition` CSS property again from the slider element.
   */
  private onSliderTransitionEnd(): void {
    this.$slider.style.transition = '';
  }

  /**
   * Called when the user starts a touch action.
   *
   * @param event The event arguments
   */
  private onTouchStart(event: TouchEvent): void {
    this.$slider.style.transition = '';

    this.isTouchCanceled = false;
    this.touchStartedAt = Date.now();
    this.touchStartX = event.touches[0].clientX;
    this.isSwiping = false;
  }

  /**
   * Called when the user moves their finger after a touch action has been
   * started.
   *
   * @param event The event arguments
   */
  private onTouchMove(event: TouchEvent): void {
    // If the user started their touch action while swiping top-to-bottom (or
    // bottom-to-top), the touch action is canceled and the slider should return
    // to its original position.
    if (this.isTouchCanceled) {
      this.moveToSlide(this.currentSlide.index);

      return;
    }

    // The user needs to move their finger at least 5 pixels in either direction
    // for the touch action to be counted as a swipe.
    if (
      !this.isSwiping
      && Math.abs(event.touches[0].clientX - this.touchStartX) > 5
    ) {
      this.isSwiping = true;
    }

    // Prevent the touch from scrolling the page if the touch action is
    // considered a swipe.
    if (this.isSwiping) {
      event.preventDefault();
    }

    // Determine the distance that the user has moved their finger, and
    // calculate a preliminary touch position so that resistance can be added if
    // necessary.
    let distance = event.touches[0].clientX - this.touchStartX;

    const minimumPosition = this.sliderOffset - (this.slideWidth * this.currentSlide.index);
    const preliminaryPosition = distance + minimumPosition;

    // Add resistance to the touch action if the user is either on the first
    // slide and swiping to the left, or on the last slide and swiping to the
    // right.
    if (
      preliminaryPosition > 0
      || preliminaryPosition < -(this.slideWidth * (this.slides.length - 1))
    ) {
      distance *= .35;
    }

    // Move the slider underneath the user's finger.
    this.touchPosition = distance + minimumPosition;
    this.$slider.style.left = `${this.touchPosition}px`;
  }

  /**
   * Called when the user lifts their finger off the screen after a touch action
   * has been started.
   *
   * @param event The event arguments
   */
  private onTouchEnd(event: TouchEvent): void {
    // If the touch action is canceled, this event handler shouldn't do
    // anything.
    if (this.isTouchCanceled) {
      return;
    }

    // Ignore the touch action if the user only moved their finger 5 pixels in
    // either direction.
    if (Math.abs(event.changedTouches[0].clientX - this.touchStartX) < 5) {
      return;
    }

    // If the duration of the touch action is less than 500 milliseconds and the
    // user moved their finger at least 50 pixels in either direction, the touch
    // action is considered a 'flick' and the slider will move in the direction
    // that the user moved their finger in.
    if (
      Date.now() - this.touchStartedAt < 500
      && Math.abs(event.changedTouches[0].clientX - this.touchStartX) > 50
    ) {
      // If the X-coordinate of where the user lifted their finger off the
      // screen is lower than the starting X-coordinate, the user swiped to the
      // left, otherwhise the user swiped to the right.
      if (event.changedTouches[0].clientX < this.touchStartX) {
        if (this.currentSlide.index < this.slides.length - 1) {
          this.moveToSlide('next');
        } else {
          this.moveToSlide(this.currentSlide.index);
        }
      } else {
        if (this.currentSlide.index !== 0) {
          this.moveToSlide('previous');
        } else {
          this.moveToSlide(this.currentSlide.index);
        }
      }

      return;
    }

    // Determine if the next (or previous) slide is halfway across the screen.
    // If it is, the slider will be moved to that slide. Otherwise the slider
    // will return to its original position.
    if (
      // If the touch position is positive, the user is trying to go to the
      // previous slide from the first slide, which isn't possible as there is
      // no previous slide.
      this.touchPosition < 0

      // If the touch position is below the width of the slider without the last
      // slide, the user is trying to go to the next slide from the last slide,
      // which isn't possible as there is no next slide.
      && this.touchPosition < -(this.slideWidth * (this.slides.length - 1))

      // The current slider offset is added to the touch position to determine
      // if the next or previous slide is halfway across the screen.
      && Math.abs(this.touchPosition + (this.slideWidth * this.currentSlide.index)) > (window.innerWidth / 2)
    ) {
      if (event.changedTouches[0].clientX < this.touchStartX) {
        this.moveToSlide('next');
      } else {
        this.moveToSlide('previous');
      }
    } else {
      this.moveToSlide(this.currentSlide.index);
    }
  }

  /**
   * Called when the user starts their touch action while swiping from
   * top-to-bottom (or bottom-to-top).
   *
   * Sets the canceled flag for the touch action, to prevent the swipe from
   * happening.
   */
  private onPointerCancel(): void {
    this.isTouchCanceled = true;
  }
}
